import { useCallback, useEffect, useMemo, useState } from "react";
import { Icon, type IconName } from "../components/Icon.js";
import { BrandMark } from "../components/BrandMark.js";
import { useToast } from "../components/Toast.js";
import { AccountMenu } from "../auth/AccountMenu.js";
import { coloringApi } from "./api.js";
import { ColoringProvider, type ColoringCtx, type ColoringTab } from "./ColoringContext.js";
import type { BookPatch, ColoringState } from "./types.js";
import { OverviewPanel } from "./OverviewPanel.js";
import { StylePanel } from "./StylePanel.js";
import { CharactersPanel } from "./CharactersPanel.js";
import { PagesPanel } from "./PagesPanel.js";
import { CoverPanel } from "./CoverPanel.js";
import { PreviewPanel } from "./PreviewPanel.js";
import { ExportPanel } from "./ExportPanel.js";

const TABS: { key: ColoringTab; label: string; icon: IconName }[] = [
  { key: "overview", label: "Overview", icon: "home" },
  { key: "style", label: "Style", icon: "layers" },
  { key: "characters", label: "Characters", icon: "users" },
  { key: "pages", label: "Pages", icon: "image" },
  { key: "cover", label: "Cover", icon: "book" },
  { key: "preview", label: "Preview", icon: "play" },
  { key: "export", label: "Export", icon: "download" }
];

/**
 * The Coloring Book workspace. Deliberately separate from the manuscript
 * Workspace (no permanent sidebar): a slim top bar with the book title and a
 * row of workflow tabs, with each tab given the whole canvas.
 */
export function ColoringWorkspace({ projectId, onGoLibrary }: { projectId: string; onGoLibrary: () => void }) {
  const toast = useToast();
  const [state, setState] = useState<ColoringState | null>(null);
  const [tab, setTab] = useState<ColoringTab>("overview");

  const reload = useCallback(async () => {
    try {
      setState(await coloringApi.state(projectId));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load the coloring book");
    }
  }, [projectId, toast]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // While anything is queued or generating, poll so progress updates live. The queue
  // state lives on the server, so a refresh simply resumes showing it.
  const generating = !!state?.jobs.some((j) => j.status === "queued" || j.status === "generating");
  useEffect(() => {
    if (!generating) return;
    const timer = setInterval(() => void reload(), 2000);
    return () => clearInterval(timer);
  }, [generating, reload]);

  const saveBook = useCallback(
    async (patch: BookPatch) => {
      try {
        const { book } = await coloringApi.updateBook(projectId, patch);
        setState((s) => (s ? { ...s, book } : s));
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Could not save.");
        throw err;
      }
    },
    [projectId, toast]
  );

  const ctx = useMemo<ColoringCtx | null>(
    () => (state ? { projectId, state, reload, saveBook, goTo: setTab } : null),
    [projectId, state, reload, saveBook]
  );

  if (!ctx) {
    return (
      <div className="auth-screen">
        <Icon name="book" size={26} className="spin" style={{ color: "var(--accent)" }} />
      </div>
    );
  }

  return (
    <ColoringProvider value={ctx}>
      <div className="coloring-app">
        <header className="topbar glass">
          <button className="btn btn-icon btn-ghost" title="Back to library" onClick={onGoLibrary}>
            <Icon name="home" size={17} />
          </button>
          <BrandMark size={30} />
          <span className="strong truncate" style={{ maxWidth: 360 }}>{ctx.state.project.title}</span>
          <span className="chip chip-accent">Coloring book</span>
          <div className="spacer" />
          <AccountMenu />
        </header>

        <nav className="coloring-tabs">
          {TABS.map((t) => (
            <button key={t.key} className={`coloring-tab ${tab === t.key ? "active" : ""}`} onClick={() => setTab(t.key)}>
              <Icon name={t.icon} size={15} />
              <span>{t.label}</span>
            </button>
          ))}
        </nav>

        <main className="coloring-main">
          {tab === "overview" && <OverviewPanel />}
          {tab === "style" && <StylePanel />}
          {tab === "characters" && <CharactersPanel />}
          {tab === "pages" && <PagesPanel />}
          {tab === "cover" && <CoverPanel />}
          {tab === "preview" && <PreviewPanel />}
          {tab === "export" && <ExportPanel />}
        </main>
      </div>
    </ColoringProvider>
  );
}
